import {motionValue, spring, springValue} from 'motion';
import {animate} from 'motion/mini';

import {slidePosition, swipeDirection} from './videoTestimonialCarousel.util.js';

/** A persistent set of cards, positioned around the active customer story. */
class VideoCarousel extends HTMLElement {
    connectedCallback() {
        this.abort = new AbortController();
        const {signal} = this.abort;
        this.stage = this.querySelector('.videoTestimonialCarousel__stage');
        this.track = this.querySelector('.videoTestimonialCarousel__track');
        this.slides = [...this.querySelectorAll('[data-slide]')];
        this.caseStudyLink = this.querySelector('[data-case-study]');
        this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
        this.active = 0;
        this.animations = new Map();
        this.dragSource = motionValue(0);
        this.dragSpring = springValue(this.dragSource, {stiffness: 300, damping: 30});
        this.stopDragEffect = this.dragSpring.on('change', (x) => {
            this.track.style.transform = `translateX(${x}px)`;
        });
        this.classList.add('is-ready');
        this.querySelector('.videoTestimonialCarousel__controls').hidden = false;
        this.querySelectorAll('[data-previous], [data-next]').forEach((button) => {
            button.hidden = this.slides.length < 2;
        });
        this.render(false);

        // Keep preview hit areas outside the moving track so repeated clicks
        // always advance in the same direction, just like the arrow controls.
        this.querySelector('[data-previous-preview]').hidden = this.slides.length < 2;
        this.querySelector('[data-next-preview]').hidden = this.slides.length < 3;
        this.querySelectorAll('[data-previous], [data-previous-preview]').forEach((button) => {
            button.addEventListener('click', () => this.go(-1), {signal});
        });
        this.querySelectorAll('[data-next], [data-next-preview]').forEach((button) => {
            button.addEventListener('click', () => this.go(1), {signal});
        });
        this.stage.addEventListener('keydown', (event) => {
            if (event.target !== this.stage || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
            event.preventDefault();
            this.go(event.key === 'ArrowLeft' ? -1 : 1);
        }, {signal});
        this.stage.addEventListener('click', (event) => {
            if (this.suppressClick) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.suppressClick = false;
            }
        }, {capture: true, signal});
        this.slides.forEach((slide) => {
            slide.querySelector('[data-play]').addEventListener('click', (event) => {
                event.preventDefault();
                this.openVideo(slide);
            }, {signal});
        });
        this.stage.addEventListener('pointerdown', (event) => this.startDrag(event), {signal});
        this.stage.addEventListener('pointermove', (event) => this.moveDrag(event), {signal});
        this.stage.addEventListener('pointerup', (event) => this.endDrag(event), {signal});
        this.stage.addEventListener('pointercancel', () => this.cancelDrag(), {signal});
        this.stage.addEventListener('lostpointercapture', () => this.cancelDrag(), {signal});
        this.stage.addEventListener('dragstart', (event) => event.preventDefault(), {signal});
        this.reducedMotion.addEventListener('change', () => {
            this.cancelDrag();
            this.render(false);
        }, {signal});
    }

    go(direction) {
        this.active = (this.active + direction + this.slides.length) % this.slides.length;
        this.render();
    }

    render(shouldAnimate = true) {
        this.slides.forEach((slide, index) => {
            const position = slidePosition(index, this.active, this.slides.length);
            const selected = position === 0;
            const adjacent = Math.abs(position) === 1;
            const target = {
                transform: `translate(calc(-50% + ${position * 92}% + ${position * 16}px), -50%) scale(${selected ? 1 : 0.84})`,
                opacity: selected ? 1 : adjacent ? 0.6 : 0,
            };
            // Motion's native path commits the browser's current transform
            // when interrupted. The hybrid JS sampler can mix matrix() and
            // translate()/scale() numbers incorrectly on the first transition.
            this.animations.get(slide)?.stop();
            if (shouldAnimate && !this.reducedMotion.matches) {
                this.animations.set(slide, animate(slide, target, {type: spring, duration: 0.5, bounce: 0.12}));
            } else {
                Object.assign(slide.style, target);
            }
            slide.style.zIndex = selected ? 2 : adjacent ? 1 : 0;
            slide.inert = !selected;
            slide.setAttribute('aria-hidden', String(!selected));
            slide.dataset.active = String(selected);
            if (!selected) {
                slide.querySelector('[data-player-host]').replaceChildren();
                slide.classList.remove('is-playing');
            }
        });
        const activeSlide = this.slides[this.active];
        const caseStudyUrl = activeSlide.dataset.caseStudyUrl;
        this.caseStudyLink.hidden = !caseStudyUrl;
        if (caseStudyUrl) {
            this.caseStudyLink.href = caseStudyUrl;
            const name = activeSlide.querySelector('.videoTestimonialCarousel__name').textContent;
            this.caseStudyLink.setAttribute('aria-label', `Read more about ${name}'s customer story`);
        } else {
            this.caseStudyLink.removeAttribute('href');
            this.caseStudyLink.removeAttribute('aria-label');
        }
        this.querySelector('[data-status]').textContent = `Customer video ${this.active + 1} of ${this.slides.length}`;
    }

    startDrag(event) {
        if (!event.isPrimary || event.button !== 0 || this.slides.length < 2) return;
        this.suppressClick = false;
        this.pointer = {id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, time: event.timeStamp, velocity: 0, dragging: false};
    }

    moveDrag(event) {
        const pointer = this.pointer;
        if (!pointer || pointer.id !== event.pointerId) return;
        const x = event.clientX - pointer.x;
        const y = event.clientY - pointer.y;
        if (!pointer.dragging) {
            if (Math.abs(y) > 8 && Math.abs(y) > Math.abs(x)) {
                this.cancelDrag();
                return;
            }
            if (Math.abs(x) <= 8) return;
            pointer.dragging = true;
            this.suppressClick = true;
            this.stage.setPointerCapture(event.pointerId);
            this.classList.add('is-dragging');
        }
        const elapsed = event.timeStamp - pointer.time;
        if (elapsed > 0) pointer.velocity = (event.clientX - pointer.lastX) / elapsed * 1000;
        pointer.lastX = event.clientX;
        pointer.time = event.timeStamp;
        if (this.reducedMotion.matches) this.dragSpring.jump(x * 0.45);
        else this.dragSource.set(x * 0.45);
    }

    endDrag(event) {
        const pointer = this.pointer;
        if (!pointer || pointer.id !== event.pointerId) return;
        if (pointer.dragging) {
            const velocity = event.timeStamp - pointer.time < 100 ? pointer.velocity : 0;
            const direction = swipeDirection(event.clientX - pointer.x, velocity);
            if (direction) this.go(direction);
        }
        this.cancelDrag();
    }

    cancelDrag() {
        const pointer = this.pointer;
        this.pointer = null;
        if (pointer && this.stage.hasPointerCapture(pointer.id)) this.stage.releasePointerCapture(pointer.id);
        this.classList.remove('is-dragging');
        this.dragSource.set(0);
        if (this.reducedMotion.matches) this.dragSpring.jump(0);
    }

    openVideo(slide) {
        const content = slide.querySelector('[data-player]').content.cloneNode(true);
        const iframe = content.querySelector('iframe');
        // Keep third-party consent handling intact; only Mux opts into autoplay.
        const url = new URL(iframe.src);
        if (url.hostname === 'player.mux.com') {
            url.searchParams.set('autoplay', 'true');
            iframe.src = url.href;
        }
        slide.querySelector('[data-player-host]').replaceChildren(content);
        slide.classList.add('is-playing');
        iframe.focus({preventScroll: true});
    }

    disconnectedCallback() {
        this.abort.abort();
        this.cancelDrag();
        this.animations.forEach((animation) => animation.stop());
        this.stopDragEffect();
        this.dragSpring.destroy();
        this.dragSource.destroy();
        this.querySelectorAll('[data-player-host]').forEach((host) => host.replaceChildren());
        this.slides.forEach((slide) => slide.classList.remove('is-playing'));
    }
}

customElements.define('fk-video-carousel', VideoCarousel);
